import { ref, reactive, onUnmounted, computed, nextTick } from "vue";
import cloneDeep from "lodash/cloneDeep";
import { FormCollaborationChannel } from "@/channels/index.js";
import INTERNAL_QUERY_PARAMS from "@/constants/internalQueryParams";

export function useCollaborativeForm(formUuid, options = {}) {
  // Reactive state
  const connectionStatus = ref("disconnected");
  const activeCollaborators = ref([]);
  const activeFields = reactive(new Map()); // Track which fields are being edited
  const collaborationChannel = ref(null);
  const currentUserId = ref(null);

  const getCollaborativeSubmissionId = () => {
    const query = new URLSearchParams(window.location.search);
    return query.get(INTERNAL_QUERY_PARAMS.collaborate);
  };

  const isCollaborativeSubmissionEnabled = computed(() => {
    return (
      options.settings.isCollaborativeSubmissionEnabled &&
      !options.isPreview &&
      !options.isEditMode
    );
  });

  // Private helper to create collaboration channel
  const createCollaborationChannel = () => {
    return new FormCollaborationChannel(
      formUuid,
      getCollaborativeSubmissionId(),
      {
        onConnectionStatusChanged: handleConnectionStatusChanged,
        onCollaboratorJoined: handleCollaboratorJoined,
        onCollaboratorLeft: handleCollaboratorLeft,
        onFieldFocused: handleFieldFocused,
        onFieldBlurred: handleFieldBlurred,
        onFieldUpdated: handleFieldUpdated,
        onFormUpdated: handleFormUpdated,
        onInitialSync: handleInitialSync,
        onSyncResponse: handleSyncResponse,
        onSessionCompleted: handleSessionCompleted,
      },
    );
  };

  // Initialize collaboration session with optional progress saving
  const startCollaborationSession = async ({ syncProgress = false } = {}) => {
    // Check if we should enable collaboration
    const shouldEnable =
      isCollaborativeSubmissionEnabled.value &&
      !!getCollaborativeSubmissionId();

    if (!shouldEnable || !formUuid) {
      return;
    }

    // Prevent multiple connections
    if (collaborationChannel.value) {
      return;
    }

    try {
      // Create channel with event callbacks
      collaborationChannel.value = createCollaborationChannel();

      // Connect to WebSocket
      await collaborationChannel.value.connect();

      if (syncProgress) {
        await saveCurrentFormProgress();
      }
    } catch (error) {
      console.error("Failed to initialize collaboration:", error);
    }
  };

  // Save current form data to collaborative session
  const saveCurrentFormProgress = async () => {
    if (!collaborationChannel.value || !getCurrentFormData) {
      return;
    }

    try {
      const currentData = getCurrentFormData();
      if (currentData && Object.keys(currentData).length > 0) {
        await collaborationChannel.value.broadcastFormUpdate(currentData);
      }
    } catch (error) {
      console.error("Failed to save form progress:", error);
    }
  };

  // Event handlers
  const handleConnectionStatusChanged = (data) => {
    connectionStatus.value = data.status;

    switch (data.status) {
      case "error":
        console.error("WebSocket connection error:", data.error);
        break;
      case "rejected":
        console.error("WebSocket connection rejected");
        break;
    }
  };

  const handleCollaboratorJoined = (data) => {
    if (data.user_id === currentUserId.value) return;

    if (data.user_name) {
      const collaborator = {
        id: data.id,
        name: data.user_name || "Anonymous User",
        color: data.user_color || "#6B7280",
        type: data.user_type || "anonymous",
      };

      // Check if collaborator already exists to prevent duplicates
      const existingIndex = activeCollaborators.value.findIndex(
        (c) => c.id === collaborator.id,
      );

      if (existingIndex === -1) {
        activeCollaborators.value.push(collaborator);
      } else {
        activeCollaborators.value[existingIndex] = collaborator;
      }
    }
  };

  const handleCollaboratorLeft = (data) => {
    const index = activeCollaborators.value.findIndex(
      (c) => c.id === data.user_id,
    );
    if (index > -1) {
      activeCollaborators.value.splice(index, 1);
    }

    // Remove any active fields locked by this collaborator
    data.collaborator_locked_fields.forEach((fieldId) => {
      activeFields.delete(fieldId);
      updateFieldLockState(fieldId);
    });
  };

  const handleFieldFocused = (data) => {
    // Don't process focus events from the current user
    if (data.user_id === currentUserId.value) {
      return;
    }

    const userInfo = {
      userId: data.user_id,
      userName: data.user_name,
      userColor: data.user_color,
    };

    activeFields.set(data.field_id, userInfo);
    updateFieldLockState(data.field_id);
  };

  const handleFieldBlurred = (data) => {
    // Don't process blur events from the current user
    if (data.user_id === currentUserId.value) {
      return;
    }

    activeFields.delete(data.field_id);
    updateFieldLockState(data.field_id);
  };

  const handleFieldUpdated = (data) => {
    // Only update if it's not from the current user
    if (data.user_id !== currentUserId.value) {
      const element = findElementByFieldId(data.field_id);
      if (element) {
        element._isCollaborativeUpdate = true;
        element.value = data.value;

        nextTick(() => {
          if (element._isCollaborativeUpdate) {
            delete element._isCollaborativeUpdate;
          }
        });
      }
    }
  };

  const handleFormUpdated = (data) => {
    // Only update if it's not from the current user
    if (data.user_id !== currentUserId.value) {
      // Apply all the form data updates
      if (data.form_data && typeof data.form_data === "object") {
        prefillFormData(data.form_data);
      }
    }
  };

  const handleInitialSync = (data) => {
    // Set current user ID from the response
    if (data.current_user && data.current_user.id) {
      currentUserId.value = data.current_user.id;
    }

    // Filter out collaborators exclude current user
    activeCollaborators.value = (data.collaborators || []).filter(
      (c) => c.id !== currentUserId.value,
    );

    // Handle locked fields data
    if (data.locked_fields && Object.keys(data.locked_fields).length > 0) {
      handleLockedFieldsData(data.locked_fields);
    }

    // Prefill form with synchronized data
    if (data.data && Object.keys(data.data).length > 0) {
      prefillFormData(data.data);
    }
  };

  const handleSyncResponse = (data) => {
    // Update collaborators list
    activeCollaborators.value = (data.collaborators || []).filter(
      (c) => c.id !== currentUserId.value,
    );

    // Handle locked fields data
    if (data.locked_fields && Object.keys(data.locked_fields).length > 0) {
      handleLockedFieldsData(data.locked_fields);
    }

    // Update form with latest data
    if (data.data && Object.keys(data.data).length > 0) {
      prefillFormData(data.data);
    }
  };

  const handleLockedFieldsData = (lockedFieldsData) => {
    // Clear existing active fields
    activeFields.clear();

    // Process locked fields data and populate activeFields
    Object.entries(lockedFieldsData).forEach(([fieldId, lockData]) => {
      if (lockData.user_id !== currentUserId.value) {
        // Find collaborator info for this user
        const collaborator = activeCollaborators.value.find(
          (c) => c.id === lockData.user_id,
        );

        const userInfo = {
          userId: lockData.user_id,
          userName: collaborator?.name || "Unknown User",
          userColor: collaborator?.color || "#6B7280",
        };

        activeFields.set(fieldId, userInfo);
        updateFieldLockState(fieldId);
      }
    });
  };

  const handleSessionCompleted = (data) => {
    // Trigger form submission with the same data to show thank you page
    if (data.submission_data && triggerThankYouPage) {
      triggerThankYouPage(data.submission_data);
    }

    // Clean up local state
    activeCollaborators.value = [];
    activeFields.clear();

    // Disconnect from collaboration
    cleanup();
  };

  // Field interaction methods
  const broadcastFieldFocus = (fieldId) => {
    if (collaborationChannel.value) {
      collaborationChannel.value.broadcastFieldFocus(fieldId);
    }
  };

  const broadcastFieldUpdate = (fieldId, value) => {
    if (collaborationChannel.value) {
      collaborationChannel.value.broadcastFieldUpdate(fieldId, value);
    }
  };

  const broadcastFieldBlur = (fieldId) => {
    if (collaborationChannel.value) {
      collaborationChannel.value.broadcastFieldBlur(fieldId);
    }
  };

  // Utility methods
  const requestSync = () => {
    if (collaborationChannel.value) {
      collaborationChannel.value.requestSync();
    }
  };

  // Update specific field lock state
  const updateFieldLockState = (fieldId) => {
    const activeField = activeFields.get(fieldId);
    const lockState = activeField && activeField.userId !== currentUserId.value;
    let userInfo;
    if (lockState) {
      userInfo = {
        userId: activeField.userId,
        userName: activeField.userName,
        userColor: activeField.userColor,
      };
    }

    const element = findElementByFieldId(fieldId);
    if (element) {
      element.locked = lockState;
      element.lockedBy = userInfo;
    }
  };

  const findElementByFieldId = (fieldId) => {
    if (!formElementMap || typeof formElementMap !== "object") {
      return null;
    }

    // Parse the fieldId as it may contain repeat field syntax
    // Example: "repeat-field-id[0][component-id]" or "field-id"
    let element;
    const regex = /^([a-f0-9-]{36})(?:\[(\d+)\])?(?:\[(.*?)\])?$/i;
    const matches = fieldId.match(regex);

    if (matches) {
      const id = matches[1];
      element = formElementMap[id];
      if (!element) return;

      if (element.type === "repeat-field") {
        const rowIndex = parseInt(matches[2], 10);
        const componentId = matches[3];

        // Ensure the row exists - if not, create it
        while (element.rows.length <= rowIndex) {
          if (element.components) {
            element.rows.push(cloneDeep(element.components));
          }
        }

        const row = element.rows[rowIndex];
        if (row) {
          element = row.find((comp) => comp.id === componentId);
        }
      }
    } else {
      element = formElementMap[fieldId];
    }

    return element;
  };

  let prefillFormData = () => {};
  let getCurrentFormData = () => {};
  let triggerThankYouPage = () => {};
  let formElementMap = {};

  const setUtilityMethods = (methods) => {
    prefillFormData = methods.prefillFormData || prefillFormData;
    getCurrentFormData = methods.getCurrentFormData || getCurrentFormData;
    triggerThankYouPage = methods.triggerThankYouPage || triggerThankYouPage;
    formElementMap = methods.formElementMap || formElementMap;
  };

  // Computed values
  const hasActiveCollaborators = computed(
    () => activeCollaborators.value.length > 0,
  );
  const isConnected = computed(() => connectionStatus.value === "connected");
  const hasConnectionError = computed(() =>
    ["error", "rejected"].includes(connectionStatus.value),
  );
  const getActiveField = (fieldId) => activeFields.get(fieldId);
  const isFieldActive = (fieldId) => activeFields.has(fieldId);

  // Cleanup
  const cleanup = () => {
    if (collaborationChannel.value) {
      collaborationChannel.value.disconnect();
      collaborationChannel.value = null;
    }
  };

  // Clean up collaborative session on form submission completion
  const cleanupCollaborativeSession = (submissionData = {}) => {
    // Send session cleanup signal to the backend with submission data
    if (collaborationChannel.value) {
      collaborationChannel.value.broadcastSessionComplete(submissionData);
    }

    // Clean up local state
    activeCollaborators.value = [];
    activeFields.clear();

    // Disconnect from collaboration channel
    cleanup();
  };

  onUnmounted(() => {
    cleanup();
  });

  return {
    // State
    isCollaborativeSubmissionEnabled: isCollaborativeSubmissionEnabled,
    connectionStatus,
    activeCollaborators,
    currentUserId,

    // Methods
    broadcastFieldFocus,
    broadcastFieldUpdate,
    broadcastFieldBlur,
    setUtilityMethods,
    requestSync,
    startCollaborationSession,
    cleanupCollaborativeSession,
    updateFieldLockState,

    // Computed
    hasActiveCollaborators,
    isConnected,
    hasConnectionError,
    getActiveField,
    isFieldActive,

    collaborationChannel,
  };
}
